class GoogleAddressElement extends HTMLElement {
    #searchService = undefined
    #placeService = undefined
    #autocomplete = undefined
    #place = undefined
    #submitTimeout = -1

    constructor() {
        super()

        console.log('GoogleAddressElement constructor called');
        console.log('Google Maps available:', typeof google !== 'undefined');
        console.log('Google Maps Places available:', typeof google?.maps?.places !== 'undefined');

        // Check if Google Maps API is loaded
        if (typeof google === 'undefined') {
            console.error('Google Maps API not loaded');
            this.#dispatchError('Google Maps API is not loaded. Please refresh the page.')
            return;
        }

        if (typeof google.maps === 'undefined' || typeof google.maps.places === 'undefined') {
            console.error('Google Maps Places API not available');
            this.#dispatchError('Google Places API is not available. Please refresh the page.')
            return;
        }

        try {
            this.#searchService = new google.maps.places.AutocompleteService()
            this.#placeService = new google.maps.places.PlacesService(document.createElement('div'))
            console.log('Google Places services initialized successfully');
        } catch(e) {
            console.error('Error initializing Google Places services:', e);
            console.error('Error details:', e.message, e.stack);
            this.#dispatchError('Google Places is not working. Please try again later.')
        }
    }   

    get input() {
        return this.querySelector('input')
    }

    get place() {
        return this.#place
    }

    connectedCallback() {
        console.log('GoogleAddressElement connectedCallback called');
        console.log('Input element found:', !!this.input);
        
        try {
            this.#autocomplete = new google.maps.places.Autocomplete(this.input)
            console.log('Autocomplete widget created successfully');
            this.#autocomplete.addListener('place_changed', this.#onDropdownSelect)
            
            this.input.addEventListener('blur', this.#onSubmit)
            this.input.addEventListener('keydown', (e) => {
                if (e.key === 'Enter') {
                    this.#onSubmit()
                    e.preventDefault()
                }
            })
        } catch (e) {
            console.error('Error creating autocomplete widget:', e);
            console.error('Error details:', e.message, e.stack);
            this.#dispatchError('Google Places is not working. Please try again later.')
        }

        this.#isGooglePlacesWorking().then((isWorking) => {
            console.log('Google Places working check result:', isWorking);
            if (!isWorking) {
                this.#dispatchError('Google Places is not working. Please try again later.')
            }
        })
    }

    #setPlace = (place) => {
        this.#place = place
        this.dispatchEvent(new CustomEvent('place_changed', {
            detail: { place }
        }))
    }

    #onDropdownSelect = () => {
        clearTimeout(this.#submitTimeout)
        const place = this.#autocomplete.getPlace()
        // prevent place from being set if the place is invalid
        if (place.address_components) {
            this.#setPlace(place)
        }
    }

    #onSubmit = () => {
        // google autocomplete can take up to half a second to trigger
        // a place_changed event when the user selects a place from the dropdown
        const WAIT_FOR_AUTOCOMPLETE_DELAY_MS = 500

        clearTimeout(this.#submitTimeout)
        this.#submitTimeout = setTimeout(async () => {
            if (!this.input.value) {
                this.#setPlace(undefined)
                return
            }

            try {
                const result = await this.#searchService.getPlacePredictions({ input: this.input.value })
                const mostLikelyResult = result.predictions[0]
    
                if (mostLikelyResult === undefined) {
                    this.#setPlace(undefined)
                    return
                }
    
                if (this.place === undefined || mostLikelyResult.place_id !== this.place.place_id) {
                    try {
                        const place = await this.#getPlaceDetails(mostLikelyResult.place_id)
                        this.input.value = place.formatted_address
                        this.#setPlace(place)
                    } catch (err) {
                        this.#setPlace(undefined)
                    }
    
                    return
                }
            } catch (e) {
                console.error('Error in address search:', e);
                console.error('Error details:', e.message, e.stack);
                this.#dispatchError('Google Places is not working. Please try again later.')
            }
        }, WAIT_FOR_AUTOCOMPLETE_DELAY_MS)
    }

    #getPlaceDetails = (id) => {
        return new Promise((resolve, reject) => {
            this.#placeService.getDetails({
                placeId: id,
                fields: ['place_id', 'geometry', 'address_components', 'types', 'formatted_address'],
            }, (place, status) => {
                if (status === google.maps.places.PlacesServiceStatus.OK) {
                    resolve(place)
                } else {
                    reject(status)
                }
            })
        })
    }

    #isGooglePlacesWorking = () => {
        // 100 Red Rock Rd, Newport, MN 55055.
        return this.#getPlaceDetails('ChIJQRUrp1TR94cRi8-5Bntl7y8')
            .then(() => true)
            .catch(() => false)
    }

    #dispatchError = (message) => {
        this.dispatchEvent(new CustomEvent('place_error', {
            detail: { message },
        }))
    }
}

window.customElements.define('google-address', GoogleAddressElement)
